import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chooseLocale, currentLocale, offeredLocales, takePickedLocale, type Locale } from "@ghezaa/ui";
import { api } from "./api";

export type UserStatus = "Pending" | "Active" | "Disabled";

/** `GET /auth/session` — available to anyone; tells the sign-in page which mode the server is in and whether to try at once. */
export type Session = {
  authenticated: boolean;
  mode: "Entra" | "Development";
  name: string | null;
  status: string | null;
  /** The device is signed in to another Ghezaa app (the `ghz.device` hint): the sign-in page goes to Microsoft with no button. */
  deviceSignedIn: boolean;
};

/** `GET /auth/me` — the signed-in person as this application's cookie knows them. Every Ghezaa app exposes it. */
export type AuthUser = {
  id: string | null;
  name: string | null;
  email: string | null;
  status: UserStatus | null;
  permissions: string[];
  /** The language they chose, kept by Core and followed by every app; null: never chose (English). */
  language: Locale | null;
  /**
   * Set while an admin views every app as a role (C4): `permissions` are then that role's and every change is refused.
   * `markets` empty: every market. `until`: when it ends by itself.
   */
  viewAs: { role: string; markets: string[]; until: string } | null;
};

export const sessionKeys = {
  session: ["auth", "session"] as const,
  me: ["auth", "me"] as const,
  apps: ["auth", "apps"] as const,
};

/**
 * Fetched afresh whenever the sign-in page shows or comes back to the front: `deviceSignedIn` decides a sign-in with no
 * button, and a cached answer from before a sign-out would undo that sign-out.
 */
export function useSession() {
  return useQuery({
    queryKey: sessionKeys.session,
    queryFn: () => api<Session>("/auth/session"),
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
}

/**
 * The signed-in person, from this app's own cookie. The server re-asks Core on the person's next request after Core lists
 * them as changed (C6), every ten minutes of use otherwise; `AuthGate` then asks this again (a disabled person gets 401
 * and the sign-in page). A session also ends when the person signs out of any Ghezaa app on this device, or signs in as
 * someone else (the `ghz.device` hint): the next call answers 401, and `createAppQueryClient` re-asks this.
 */
export function useAuthUser() {
  return useQuery({ queryKey: sessionKeys.me, queryFn: () => api<AuthUser>("/auth/me") });
}

/** `GET /auth/apps` — an application the person can open, for the switcher (C7). `current`: the one answering. */
export type LauncherApp = { code: string; name: string; description: string; url: string; current: boolean };

/**
 * The applications the person can open, asked of Core each time the switcher opens (`enabled`), so a role given a moment
 * ago is there; the last list shows meanwhile.
 */
export function useApplications(enabled: boolean) {
  return useQuery({ queryKey: sessionKeys.apps, queryFn: () => api<LauncherApp[]>("/auth/apps"), enabled, staleTime: 0 });
}

/** Saves the person's language (in Core, through this app) and in this session at once. */
export function useSetLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (language: Locale | null) => api<AuthUser>("/auth/language", { method: "PUT", json: { language } }),
    onSuccess: (me) => queryClient.setQueryData(sessionKeys.me, me),
  });
}

/**
 * Makes the page follow the signed-in person's language (docs/languages-plan.md, rule 1): a language picked with the
 * switch before signing in becomes theirs; otherwise the one they chose in any app. Only a language this app offers
 * (an app not yet translated stays in English). Returns true while the page reloads into it. The pseudo-locale is left alone.
 */
export function useFollowLanguage(me: AuthUser | undefined) {
  const save = useSetLanguage();
  const picked = useRef<Locale | null | undefined>(undefined);
  if (picked.current === undefined) picked.current = takePickedLocale();
  const adopted = useRef(false);
  const current = currentLocale();

  const choice = picked.current ?? me?.language ?? null;
  const wanted = !me || current === "pseudo" || !choice || !offeredLocales().includes(choice) ? null : choice;
  const switching = wanted !== null && wanted !== current;

  useEffect(() => {
    if (!me || adopted.current || !picked.current || picked.current === me.language) return;
    adopted.current = true;
    save.mutate(picked.current);
  }, [me, save]);

  useEffect(() => {
    if (switching && wanted) chooseLocale(wanted);
  }, [switching, wanted]);

  return switching;
}

/**
 * Stops viewing as a role, in every app (Core keeps the choice): this app's session takes the person's own permissions
 * back at once, and the page reloads so every screen asks again with them.
 */
export function useEndViewAs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<AuthUser>("/auth/view-as", { method: "DELETE" }),
    onSuccess: () => {
      queryClient.clear();
      window.location.reload();
    },
  });
}

/**
 * Ends the person's session on this device and reloads to this app's sign-in page (`home`: "/" or, for Orders' back
 * office, "/staff"). The other Ghezaa apps on this device end at their next request (the server forgets the `ghz.device`
 * hint); other devices, and the Microsoft 365 session itself, are left alone: sign-out is how people switch accounts.
 */
export function useSignOut(home = "/") {
  const queryClient = useQueryClient();
  return async () => {
    await api<void>("/auth/logout", { method: "POST" });
    queryClient.clear();
    window.location.assign(home);
  };
}

/**
 * The query key the server leaves on the sign-in page: `?signin=failed` after a Microsoft callback that went wrong,
 * `?signin=auto` after the attempt made on arrival with no button (so a 401 after it shows the button, not another attempt).
 */
export const SIGN_IN_MARKER = "signin";

export type SignInMarker = "failed" | "auto";

export function signInMarker(): SignInMarker | null {
  const value = new URLSearchParams(window.location.search).get(SIGN_IN_MARKER);
  return value === "failed" || value === "auto" ? value : null;
}

export function signInFailed(): boolean {
  return signInMarker() === "failed";
}

/** The page the person wanted, without the marker, so it is not carried into the next sign-in. */
export function returnUrlWithoutMarker() {
  const params = new URLSearchParams(window.location.search);
  params.delete(SIGN_IN_MARKER);
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ""}`;
}

/** The sign-in address; `auto` is the attempt made on arrival (no account picker, the page marked `?signin=auto`). */
export function signInHref(returnUrl = returnUrlWithoutMarker(), options: { auto?: boolean } = {}) {
  return `/auth/login?returnUrl=${encodeURIComponent(returnUrl)}${options.auto ? "&auto=true" : ""}`;
}
